/** Both outboxes: sections / locations (reused mobile store) and SOS / turnout. */
import { useState } from "react";
import type { OutboxItem, SectionOutboxItem } from "@core/types";
import { outboxErrorText } from "@core/messages";
import type { PollingItem } from "../field/polling";
import { categoryLabel, formatTime, type Strings } from "../i18n";
import { useApp, useQuery, useSession, useSyncStatus } from "../ui/context";
import { href } from "../ui/router";
import { Banner, Button, Card, StatusPill } from "../ui/components";
import { slotLabel } from "../field/time";

export function SyncScreen() {
  const { app, t, lang } = useApp();
  const status = useSyncStatus();
  const items = useQuery(async () => ({ core: await app.core.store.listOutbox(), polling: await app.polling.list() }), []);
  const c = status.counts;

  return (
    <div data-testid="sync-screen">
      <h2>{t.syncTitle}</h2>
      <div style={{ marginBottom: 16 }} data-testid="sync-summary">
        {c.total === 0 ? <div style={{ fontSize: 22, fontWeight: 900, color: "var(--ok)" }}>✓ {t.syncAllDone}</div> : null}
        {c.pending ? <div style={{ fontSize: 20, fontWeight: 800 }}>{t.syncPendingN(c.pending)}</div> : null}
        {c.failed ? <div style={{ fontSize: 20, fontWeight: 800, color: "var(--danger)" }}>{t.syncFailedN(c.failed)}</div> : null}
        {c.conflict ? <div style={{ fontSize: 20, fontWeight: 800, color: "#6a1b9a" }}>{t.syncConflictN(c.conflict)}</div> : null}
        {status.lastRunAt ? <div className="muted">{t.syncLastRun(formatTime(status.lastRunAt, lang))}</div> : null}
        {status.nextRetryAt ? <div className="muted">{t.syncNextRetry(formatTime(status.nextRetryAt, lang))}</div> : null}
      </div>
      {status.online === false ? <Banner kind="warn">{t.syncOfflineNote}</Banner> : null}
      <Button big busy={status.running} disabled={c.pending === 0 && !status.running} onClick={() => void app.syncNow("manual")} testId="sync-now">
        ⟳ {status.running ? t.syncing : t.syncNow}
      </Button>
      <div style={{ height: 16 }} />
      {(items?.polling ?? []).map((i) => (
        <PollingCard key={`p${i.id}`} item={i} t={t} />
      ))}
      {(items?.core ?? []).map((i) => (
        <CoreCard key={`c${i.id}`} item={i} t={t} />
      ))}
      <Account />
    </div>
  );
}

/** Who is logged in, assembly switch (admins), logout. Kept here so it is never tapped by accident. */
function Account() {
  const { app, t } = useApp();
  const { user, assemblyId } = useSession();
  if (!user) return null;
  const canSwitch = user.role !== "surveyor" && (user.assemblyIds == null || user.assemblyIds.length > 1);
  return (
    <Card>
      <b>{user.name}</b>
      <div className="muted">
        {user.email ?? user.phone} • {assemblyId}
      </div>
      <div className="stack" style={{ marginTop: 12 }}>
        {canSwitch ? (
          <a className="btn btn-secondary" href={href({ name: "assembly" })}>
            ⇄ {t.changeAssembly}
          </a>
        ) : null}
        <Button variant="danger" onClick={() => window.confirm(t.logoutConfirm) && void app.logout()} testId="logout">
          {t.logout}
        </Button>
      </div>
    </Card>
  );
}

function PollingCard({ item, t }: { item: PollingItem; t: Strings }) {
  const { app, lang } = useApp();
  return (
    <Card testId={`sync-item-${item.kind}`}>
      <b>
        {t.boothNo} {item.boothCode} — {item.kind === "sos" ? `🚨 ${t.kindSos}: ${categoryLabel(t, item.payload.category)}` : `${t.kindTurnout} ${slotLabel(item.payload.slot, lang)}: ${item.payload.votesPolled}`}
      </b>
      <div>
        <StatusPill status={item.status === "pending" ? "waiting" : "failed"} t={t} />
      </div>
      <div className="muted">
        {formatTime(item.updatedAt, lang)}
        {item.attempts ? ` • ${t.attempts(item.attempts)}` : ""}
      </div>
      {item.lastError ? <ErrorLine t={t} err={item.lastError} pending={item.status === "pending"} /> : null}
      {item.status === "failed" ? (
        <div className="row" style={{ marginTop: 10 }}>
          <Button onClick={() => void app.polling.retry(item.id).then(() => app.pollingSync.trigger("manual"))}>{t.retry}</Button>
          <Button variant="danger" onClick={() => window.confirm(t.discardConfirm) && void app.polling.discard(item.id)}>
            {t.discard}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/** The API's own words in the app's language when it sent them (messageHi / message), else ours. */
function ErrorLine({ t, err, pending }: { t: Strings; err: { code: string; message: string; messageHi?: string }; pending: boolean }) {
  const { lang } = useApp();
  const text = err.code === "network" ? t.err_network : err.code === "not_found" ? t.err_not_found : outboxErrorText(err, lang);
  return <div className={pending ? "muted" : "error"}>{text}</div>;
}

function CoreCard({ item, t }: { item: OutboxItem; t: Strings }) {
  const { app, lang } = useApp();
  const [open, setOpen] = useState(item.status === "conflict");
  const title = item.kind === "location" ? `📍 ${t.kindLocation}` : t[`sec_${item.section}` as `sec_identity`];
  const ui = item.status === "pending" ? "waiting" : item.status;

  return (
    <Card testId={`sync-item-${item.kind}`}>
      <b>
        {t.boothNo} {item.boothCode} — {title}
      </b>
      <div>
        <StatusPill status={ui} t={t} />
      </div>
      <div className="muted">
        {formatTime(item.updatedAt, lang)}
        {item.attempts ? ` • ${t.attempts(item.attempts)}` : ""}
      </div>
      {item.lastError && item.status !== "conflict" ? <ErrorLine t={t} err={item.lastError} pending={item.status === "pending"} /> : null}
      {item.lastError?.details?.map((d, i) => (
        <div key={i} className="error">
          • {d.path}: {d.message}
        </div>
      ))}
      {item.kind === "location" ? (
        <div className="muted">
          {item.payload.lat.toFixed(6)}, {item.payload.lng.toFixed(6)} • ±{item.payload.accuracyM ?? "?"} {t.metres}
        </div>
      ) : null}

      {item.status === "conflict" && item.kind === "section" ? (
        <>
          <p style={{ fontWeight: 700 }}>{t.conflictExplain}</p>
          {open ? <Compare item={item} t={t} /> : null}
          <Button variant="ghost" onClick={() => setOpen(!open)}>
            {open ? t.hideDetails : t.showDetails}
          </Button>
          <div className="row" style={{ marginTop: 10 }}>
            <Button onClick={() => window.confirm(t.keepMineConfirm) && void app.core.store.keepMine(item.id).then(() => app.core.sync.trigger("manual"))} testId="keep-mine">
              {t.keepMine}
            </Button>
            <Button variant="secondary" onClick={() => window.confirm(t.useServerConfirm) && void app.core.store.useServerCopy(item.id).then(() => app.core.sync.refreshCounts())} testId="use-server">
              {t.useServer}
            </Button>
          </div>
        </>
      ) : null}

      {item.status === "failed" ? (
        <div className="row" style={{ marginTop: 10 }}>
          <Button onClick={() => void app.core.store.retry(item.id).then(() => app.core.sync.trigger("manual"))}>{t.retry}</Button>
          <Button variant="danger" onClick={() => window.confirm(t.discardConfirm) && void app.core.store.discard(item.id).then(() => app.core.sync.refreshCounts())}>
            {t.discard}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function Compare({ item, t }: { item: SectionOutboxItem; t: Strings }) {
  const { lang } = useApp();
  const s = item.serverCopy;
  return (
    <div>
      <div className="label" style={{ color: "var(--primary)" }}>
        {t.mine}
      </div>
      <pre className="copy">{summarize(item.payload.data)}</pre>
      <div className="label" style={{ color: "var(--primary)", marginTop: 8 }}>
        {t.theirs}
        {s?.updatedBy ? ` (${t.updatedBy(s.updatedBy, formatTime(s.updatedAt, lang))})` : ""}
      </div>
      <pre className="copy">{s ? summarize(s.data) : t.emptyValue}</pre>
    </div>
  );
}

/** Flat readable text of section data, enough to compare two copies. */
export function summarize(data: unknown, indent = ""): string {
  if (data == null) return "—";
  if (Array.isArray(data)) return data.length ? data.map((x, i) => `${indent}${i + 1}. ${summarize(x, indent + "   ").trimStart()}`).join("\n") : "—";
  if (typeof data === "object") {
    return Object.entries(data as Record<string, unknown>)
      .filter(([, v]) => v !== undefined && v !== "")
      .map(([k, v]) => (v && typeof v === "object" ? `${indent}${k}:\n${summarize(v, indent + "   ")}` : `${indent}${k}: ${String(v)}`))
      .join("\n");
  }
  return String(data);
}
